// frontend/src/dev/DevToolsPanel.tsx
import React, { useEffect, useState } from 'react'
import { apiJson } from '@/client'
import { Card } from '@/shared/components/Card'
import { Chip } from '@/shared/components/Chip'

type Health = { ok?: boolean; status?: string } | Record<string, any>
type Flag = { id: string; key: string; enabled_global?: boolean }

export const DevToolsPanel: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [info, setInfo] = useState<{ base: string; health: Health } | null>(null)
  const [flags, setFlags] = useState<Flag[]>([])
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '`') setOpen(v => !v)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    let alive = true
    ;(async () => {
      setErr(null)
      try {
        // Show the same base our client will use
        const base = ((import.meta as any)?.env?.VITE_API_BASE as string | undefined) ?? '/v1'

        // NOTE: client.ts adds /v1 → use base-less paths here
        const health = await apiJson<Health>('/healthz', {}, false).catch(() => ({ status: 'unknown' }))

        // Never include /v1 here; apiJson will prefix
        const feats = await apiJson<Flag[]>('/admin/feature-flags', {}, false).catch(() => [])

        if (!alive) return
        setInfo({ base, health })
        setFlags(Array.isArray(feats) ? feats : [])
      } catch (e: any) {
        if (!alive) return
        setErr(e?.message || 'Dev panel load failed')
        setInfo({
          base: ((import.meta as any)?.env?.VITE_API_BASE as string | undefined) ?? '/v1',
          health: { status: 'unknown' },
        })
        setFlags([])
      }
    })()
    return () => {
      alive = false
    }
  }, [open])

  if (!open) return null

  const status =
    (info?.health as any)?.status ??
    ((info?.health as any)?.ok ? 'ok' : (info?.health ? 'unknown' : 'unknown'))

  return (
    <div className="fixed bottom-4 right-4 w-[520px] max-w-[95vw] z-40">
      <Card title="Developer Mode (Ctrl/⌘+`)" className="border border-gray-200 bg-white/95 backdrop-blur">
        <div className="space-y-2">
          {err && (
            <div className="text-xs rounded border border-yellow-300 bg-yellow-50 p-2 text-yellow-800">
              {err}
            </div>
          )}
          <div className="text-sm">
            API Base: <code>{info?.base}</code>
          </div>
          <div className="text-sm">
            Health:{' '}
            <Chip
              label={String(status)}
              tone={String(status).toLowerCase() === 'ok' ? 'success' : 'warn'}
            />
          </div>
          <div>
            <h4 className="font-medium mb-1">Feature Flags</h4>
            {flags.length === 0 ? (
              <div className="text-sm text-gray-600">No flags</div>
            ) : (
              <ul className="text-sm list-disc pl-5">
                {flags.map((f) => (
                  <li key={f.id || f.key}>
                    <code>{f.key}</code> — global: {String(!!f.enabled_global)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}